/**
 * <s-read-more> — Clamps long text with a "Read more" / "Read less" toggle.
 *
 * The content stays in the page (good for SEO); it is only visually clamped by CSS.
 * If the text already fits, the clamp is dropped (data-fits) and the button stays hidden.
 *
 * Required child markup:
 *   [data-read-more-content] — clamped element; gets data-expanded / data-fits.
 *   [data-read-more-toggle]  — button with aria-controls, data-more-label, data-less-label.
 */
class SReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;

    this.toggle.addEventListener('click', this.#onToggle);
    requestAnimationFrame(this.#measure);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#onToggle);
  }

  #measure = () => {
    const overflowing = this.content.scrollHeight > this.content.clientHeight + 4;
    if (overflowing) {
      this.toggle.hidden = false;
    } else {
      this.content.toggleAttribute('data-fits', true);
    }
  };

  #onToggle = () => {
    const expanded = !this.content.hasAttribute('data-expanded');
    this.content.toggleAttribute('data-expanded', expanded);
    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.toggle.textContent = expanded ? this.toggle.dataset.lessLabel : this.toggle.dataset.moreLabel;
    if (!expanded) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.content.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }
  };
}

if (!customElements.get('s-read-more')) {
  customElements.define('s-read-more', SReadMore);
}
